import { ConfigProvider, Tabs, theme } from 'antd';
import { observer } from 'mobx-react-lite';
import { useRouter } from 'next/router';
import { CSSProperties, PropsWithChildren, useEffect, useMemo, useRef } from 'react';
import { InternalEventType } from '../../clients/chat/types';
import AddButton from '../../components/Tabs/AddButton';
import TabView from '../../components/Tabs/TabView';
import useBulkTicketActivities from '../../components/TicketActivityIndicator/hook/useBulkTicketActivities';
import ActiveChatTabsController from '../../components/plugins/chat/ActiveChatController';
import useToggle from '../../hooks/useToggle';
import useUploadRequestAbort from '../../hooks/useUploadRequestAbort';
import { Tab, tabTypeMap, type ChatTicketTab } from '../../model/types';
import { getChatHistoryByTicketKey } from '../../services/apps/plugins';
import { useTabStore } from '../../store/RootStoreContext';
import { createURLFromTab, getSelectedTabKeyFromUrl } from '../../store/childStore/tabStore';
import CurrentChatContextProvider, {
    useCurrentChatSessionContext
} from '../../store/provider/CurrentChatContextProvider';
import { SIDEBAR_PLUGIN_WIDTH, TAB_BAR_HEIGHT } from '../../styles/theme';
import { nullishValue } from '../../utils/helpers';
import { isPagePluginPage, isTicketsPage, pushTo } from '../../utils/routeHelper';
import { StorageHelperKeys, getSessionStorageItem } from '../../utils/storageHelpers';
import RightTopActions from '../Pages/TicketPages/TicketPagesContentLayout/RightTopActions';
import PagePluginsContainer from '../plugins/PagePluginsContainer';
import SidebarPluginContainer from '../plugins/SidebarPluginContainer';
import TranslatePluginContainer from '../plugins/TranslatePluginContainer';

// type actionType = keyof typeof ActionTypeMap;
enum ActionTypeMap {
    add = 'add',
    remove = 'remove'
}

const styles = {
    rootTab: { height: TAB_BAR_HEIGHT, margin: 0 },
    tabContent: { height: `calc(100vh - ${TAB_BAR_HEIGHT}px)`, overflowY: 'auto' } as CSSProperties
};
const isTabRelatedPageUrl = (url: string) => {
    return !!Object.keys(tabTypeMap).find((tab) => {
        return url.startsWith(`/${tab.toLocaleLowerCase()}s/`);
    });
};

function TabsController({ tabs }: { tabs: Tab[] }) {
    const {
        token: { colorFillTertiary }
    } = theme.useToken();
    const { asPath } = useRouter();

    const { closeTabAndGetNewTargetUrl, getTabByTabKey } = useTabStore();

    const source = useUploadRequestAbort();

    const selectedTabKey = getSelectedTabKeyFromUrl(asPath);

    const openedTicketKeyList: string = getSessionStorageItem(StorageHelperKeys.OPEN_TAB_KEYS)
        ?.split(',')
        ?.filter((tabKey: string) => tabKey.startsWith('TICKET'))
        ?.toString();

    useBulkTicketActivities(openedTicketKeyList, true);

    const onTabChange = (activeKey: string): void => {
        if (activeKey.startsWith('newTicket')) {
            const [, no] = activeKey.split('newTicket');
            pushTo(`/tickets/new/${no}`);
            return;
        }
        if (selectedTabKey !== activeKey) {
            const url = createURLFromTab(getTabByTabKey(activeKey));
            url && pushTo(url);
        }
    };

    const removeTab = (tabKeyToClose: string): void => {
        const targetUrl = closeTabAndGetNewTargetUrl(tabKeyToClose);
        source.cancel(tabKeyToClose);
        targetUrl && pushTo(targetUrl);
    };

    const tabItems = useMemo(
        () =>
            tabs?.map((tab) => ({
                key: tab.key,
                label: <TabView tab={tab} />
            })),
        [tabs]
    );

    return (
        <ConfigProvider
            theme={{
                components: {
                    Tabs: {
                        // add-button zero-padding
                        paddingXS: 0,
                        colorFillAlter: colorFillTertiary
                    }
                }
            }}
        >
            <Tabs
                activeKey={selectedTabKey}
                addIcon={<AddButton />}
                animated={false}
                onChange={onTabChange}
                onEdit={(targetKey: string, action) => action === ActionTypeMap.remove && removeTab(targetKey)}
                tabBarExtraContent={<RightTopActions />}
                tabBarStyle={styles.rootTab}
                type="editable-card"
                items={tabItems}
            />
        </ConfigProvider>
    );
}

function ChatHistoryController() {
    const { asPath } = useRouter();
    const { chatSessionId, messages, subscribedToChatSession } = useCurrentChatSessionContext();
    const fetchHistoryOnReconnection = useRef(false);
    const { getTabByTabKey } = useTabStore();

    const selectedTabKey = getSelectedTabKeyFromUrl(asPath);
    const isChatTicketTab = (getTabByTabKey(selectedTabKey) as ChatTicketTab)?.channel === 'CHAT';

    useEffect(() => {
        if (subscribedToChatSession) {
            fetchHistoryOnReconnection.current = true;
        }
    }, [subscribedToChatSession]);

    useEffect(() => {
        if (isChatTicketTab && subscribedToChatSession && fetchHistoryOnReconnection.current) {
            getChatHistoryByTicketKey(selectedTabKey)
                .then((data) => {
                    if (Array.isArray(data.history) && data.history.length > 0) {
                        window.dispatchEvent(
                            new CustomEvent(`${InternalEventType.CHAT_HISTORY_READY}-${selectedTabKey}`, {
                                detail: data.history
                            })
                        );
                    }
                })
                .catch((error) => console.error(error));
        }
    }, [isChatTicketTab, subscribedToChatSession, selectedTabKey]);

    const fetchInitialChatHistoryFlag =
        isChatTicketTab && !subscribedToChatSession && nullishValue(chatSessionId) && messages.length === 0;

    useEffect(() => {
        if (fetchInitialChatHistoryFlag) {
            getChatHistoryByTicketKey(selectedTabKey)
                .then((data) => {
                    if (Array.isArray(data.history) && data.history.length > 0) {
                        window.dispatchEvent(
                            new CustomEvent(`${InternalEventType.CHAT_HISTORY_READY}-${selectedTabKey}`, {
                                detail: data.history
                            })
                        );
                    }
                })
                .catch((error) => console.error(error));
        }
    }, [fetchInitialChatHistoryFlag, selectedTabKey]);

    return null;
}

export default observer(function TabViewContainer({ children }: PropsWithChildren) {
    const {
        token: { motionDurationSlow }
    } = theme.useToken();

    const { asPath } = useRouter();
    const [collapsed, toggle, collapsePluginSidebar] = useToggle(true);
    const { tabs, updateTabUrlByUrl } = useTabStore();

    useEffect(() => {
        isTabRelatedPageUrl(asPath) && updateTabUrlByUrl(asPath);

        if (isTicketsPage(asPath) || isPagePluginPage(asPath)) return;
        collapsePluginSidebar();
    }, [asPath, collapsePluginSidebar, updateTabUrlByUrl]);

    return (
        <>
            <SidebarPluginContainer isOpen={!collapsed} onButtonClick={toggle} />
            <PagePluginsContainer isCollapsed={collapsed} />
            <TranslatePluginContainer />
            <ActiveChatTabsController tabs={tabs}>
                <CurrentChatContextProvider>
                    <TabsController tabs={tabs} />
                    <ChatHistoryController />
                    <div
                        className="tab-content-container"
                        style={{
                            height: `calc(100vh - ${TAB_BAR_HEIGHT}px)`,
                            overflowY: 'auto',
                            transition: `all ${motionDurationSlow}`,
                            width: !collapsed ? `calc(100% - ${SIDEBAR_PLUGIN_WIDTH}px)` : '100%'
                        }}
                    >
                        {children}
                    </div>
                </CurrentChatContextProvider>
            </ActiveChatTabsController>
        </>
    );
});
